import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import { faClose } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { ButtonWithPopover, Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { CustomPriority } from '@/tools/thresholdMetric/metric.types';
import type { TooltipPosition } from '@seeqdev/qomponents';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface PriorityPickerProps {
  onChange: (value: number) => void;
  placement: Placement;
  buttonTooltip?: string;
  buttonTooltipPlacement?: TooltipPosition;
  buttonTestId: string;
  buttonClasses: string;
  buttonXstyle?: StyleXStyles;
  value: CustomPriority[];
}

export const PriorityPicker = ({
  onChange,
  buttonTooltip,
  buttonTooltipPlacement,
  placement,
  buttonTestId,
  buttonClasses,
  buttonXstyle,
  value: priorities,
}: PriorityPickerProps) => {
  const { t } = useTranslation();

  const [showPriorityPicker, setShowPriorityPicker] = useState(false);

  const selectPriority = (event: React.MouseEvent, priority: CustomPriority) => {
    onChange(priority.level);
    isPriorityAvailable();
    event.stopPropagation();
  };

  /**
   * Determines if a priority is is still available (i.e. they are not already all in use)
   *
   * @returns true if available, false otherwise.
   */
  const isPriorityAvailable = (): boolean => _.chain(priorities).reject({ level: 0 }).some({ inUse: false }).value();

  return (
    <div {...stylex.props(styles.picker)}>
      <ButtonWithPopover
        isOpen={showPriorityPicker}
        onOpenChange={setShowPriorityPicker}
        trigger={
          <div data-testid={buttonTestId}>
            <FakeLink>
              <Icon
                icon={buttonClasses}
                xstyle={[styles.triggerIcon, buttonXstyle]}
                tooltip={buttonTooltip === undefined ? '' : t(buttonTooltip)}
                tooltipPlacement={buttonTooltipPlacement}
              />
            </FakeLink>
          </div>
        }
        hasArrow
        isCloseOnContentClick
        placement={placement}>
        <div id="priorityPickerPopover" data-testid="priorityPickerPopover" {...stylex.props(styles.popover)}>
          <div {...stylex.props(styles.heading)}>{t('INVESTIGATE_TOOLS.THRESHOLD_METRIC.CHOOSE_PRIORITY_LEVEL')}</div>
          <div {...stylex.props(styles.content)}>
            <div {...stylex.props(styles.closeAction)}>
              <Icon testId="closePriorityPicker" onClick={() => setShowPriorityPicker(false)} icon={faClose} />
            </div>
            <div {...stylex.props(styles.columnHeadings)}>
              <div {...stylex.props(styles.swatchHeading)}>{t('INVESTIGATE_TOOLS.THRESHOLD_METRIC.DEFAULT_COLOR')}</div>
              <div {...stylex.props(styles.nameHeading)}>{t('NAME')}</div>
            </div>
            <div {...stylex.props(styles.priorities)}>
              {_.map(
                priorities.filter((priority) => priority.level),
                (priority) => (
                  <div
                    key={priority.level}
                    data-testid={`${priority.name}ThresholdPriority`}
                    {...stylex.props(styles.priority, !priority.inUse && styles.availablePriority)}
                    onClick={(event) => selectPriority(event, priority)}>
                    <div className={priority.inUse ? 'selectedOverlay' : undefined} />
                    <div {...stylex.props(styles.swatch(priority.color))} />
                    <div {...stylex.props(styles.priorityName)}>{priority.name}</div>
                  </div>
                ),
              )}
              {!isPriorityAvailable() && (
                <div {...stylex.props(styles.unavailableOverlay)}>
                  <div {...stylex.props(styles.unavailableMessage)}>
                    {t('INVESTIGATE_TOOLS.THRESHOLD_METRIC.ALL_PRIORITIES_IN_USE')}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </ButtonWithPopover>
    </div>
  );
};

const styles = stylex.create({
  picker: { cursor: 'pointer', width: 25 },
  triggerIcon: { marginRight: 5 },
  popover: { minWidth: 215, backgroundColor: colors.panelBackground, color: colors.bodyText },
  heading: {
    borderBottomStyle: 'none',
    fontSize: 'inherit',
    fontWeight: 'bolder',
    textAlign: 'center',
    padding: '0.3125rem',
    backgroundColor: colors.popoverHeading,
    color: colors.bodyText,
  },
  content: { padding: 5 },
  closeAction: { position: 'absolute', right: '0.625rem', top: 7, pointerEvents: 'auto' },
  columnHeadings: {
    display: 'flex',
    flexDirection: 'row',
    fontWeight: 'bolder',
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: colors.legacyToolsGroupBorder,
  },
  swatchHeading: { whiteSpace: 'nowrap' },
  nameHeading: { flex: '1 1 auto', overflow: 'hidden', textAlign: 'center' },
  priorities: { display: 'flex', flexDirection: 'column', position: 'relative', maxHeight: 275, overflowY: 'auto' },
  priority: {
    display: 'flex',
    flexDirection: 'row',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.toolsDisabledBorder,
    position: 'relative',
    flex: 'none',
  },
  availablePriority: {
    cursor: 'pointer',
    borderColor: {
      'default': colors.toolsDisabledBorder,
      ':hover': colors.legacyToolsPrimary,
      ':focus': colors.legacyToolsPrimary,
    },
  },
  swatch: (color: string) => ({ width: 70, backgroundColor: color }),
  priorityName: {
    flex: '1 1 auto',
    overflow: 'hidden',
    wordBreak: 'break-all',
    overflowWrap: 'break-word',
    whiteSpace: 'normal',
    paddingLeft: '0.3125rem',
  },
  unavailableOverlay: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unavailableMessage: { color: colors.mutedForeground, marginTop: '0.3125rem', fontStyle: 'italic' },
});
